import React, { useEffect, useState } from "react";
import { ChartContainer } from "../view/ChartContainer";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { CustomTooltip } from "./components/CustomTooltip";
import { transformPieChartData } from "./utils/chartDataTransformers";
import { STATUS_COLORS } from "./constants/chartColors";
import * as statisticService from "./statisticService";
import { DeviceStatus } from "../vars";
import { useHistory } from "react-router-dom";
import { InteractiveLegend } from "./components/InteractiveLegend";
import DashboardChartSkeleton from "./components/DashboardChartSkeleton";


function StatusChart() {

    const history = useHistory();

    const [rechartsData, setRechartsData] = useState([]);
    const [hiddenSeries, setHiddenSeries] = useState({});
    const [allLegendItems, setAllLegendItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const sData = await statisticService.getStatusData();
            if (sData && sData.labels && sData.datasets) {
                // Transform data for Recharts and add colors
                const transformed = transformPieChartData(sData).map(item => ({
                    ...item,
                    color: item.name === "Connected" ? STATUS_COLORS.connected : STATUS_COLORS.disconnected
                }));
                setRechartsData(transformed);
                // Store all items for legend
                setAllLegendItems(transformed.map(item => ({
                    value: item.name,
                    color: item.color,
                    dataKey: item.name
                })));
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [])

    // Handle legend click to toggle series visibility
    const handleToggleSeries = (dataKey) => {
        setHiddenSeries(prev => ({
            ...prev,
            [dataKey]: !prev[dataKey]
        }));
    };

    // Handle pie chart click
    const handlePieClick = (data, index) => {
        if (!data || !data.name) return;

        const clickedLabel = data.name;

        // Map labels to DeviceStatus and page titles
        const statusMap = {
            "Connected": {
                status: DeviceStatus.CONNECTED,
                pageTitle: "Connected Devices"
            },
            "Disconnected": {
                status: DeviceStatus.DISCONNECTED,
                pageTitle: "Disconnected Devices"
            },
            "Unknown": {
                status: DeviceStatus.UNKNOWN,
                pageTitle: "Unknown Status Devices"
            }
        };

        if (statusMap[clickedLabel]) {
            const { status, pageTitle } = statusMap[clickedLabel];
            history.push(`/devices?pageTitle=${pageTitle}&f_status=${status}`);
        }
    };

    if (isLoading) {
        return <DashboardChartSkeleton title="Status" subtitle="Devices" isPieChart={true} />;
    }

    return (
        <ChartContainer collapsable={true} noHeader={true} className="oct_chart-card-v2">
            <div className="oct_chart-header-v2">
                <p className="oct_chart-subtitle-v2">Devices</p>
                <h3 className="oct_chart-title-v2">Status</h3>
            </div>
            <div className="oct_interactive-legend-wrapper-v2">
                {allLegendItems.length > 0 && (
                    <InteractiveLegend
                        payload={allLegendItems}
                        hiddenSeries={hiddenSeries}
                        onToggleSeries={handleToggleSeries}
                    />
                )}
            </div>
            {(() => {
                const totalValue = rechartsData.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
                const isEmpty = totalValue === 0;

                return (
                    <div className="oct_recharts-wrapper-v2">
                        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 10, height: 10 }}>
                            <PieChart className={isEmpty ? "oct_empty-chart-data" : ""}>
                                <Pie
                                    data={isEmpty ? [{ name: 'No Status Data', value: 1, color: '#e2e8f0', isEmptyState: true }] : rechartsData.filter(entry => !hiddenSeries[entry.name])}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={isEmpty ? 0 : 2}
                                    dataKey="value"
                                    onClick={isEmpty ? undefined : handlePieClick}
                                    isAnimationActive={!isEmpty}
                                >
                                    {(isEmpty ? [{ name: 'No Status Data', value: 1, color: '#e2e8f0', isEmptyState: true }] : rechartsData.filter(entry => !hiddenSeries[entry.name])).map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                );
            })()}
        </ChartContainer>
    )

}

export default StatusChart;